import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { X, History, LogOut, User, Trash2 } from "lucide-react";
import { BrandSwitcher } from "./BrandSwitcher";
import { OrgSwitcher } from "@/components/admin/OrgSwitcher";
import { useAuth } from "@/auth/useAuth";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectRecentSearches } from "@/store/selectors";
import { clearRecent } from "@/store/slices/recentSearchesSlice";
import { cn, formatDateRange } from "@/lib/utils";

interface Props {
  agencyName: string;
  userInitials?: string;
}

export function CornerChrome({ agencyName, userInitials }: Props) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const location = useLocation();
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { logout } = useAuth();

  React.useEffect(() => {
    setMenuOpen(false);
    setHistoryOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <>
      {/* Top-left: brand + agency + org switch. OrgSwitcher self-hides for
          single-org users, so this stays a plain brand for everyone else. */}
      <div className="pointer-events-none absolute left-6 top-6 z-30 flex items-center gap-3">
        <BrandSwitcher agencyName={agencyName} />
        <div className="pointer-events-auto">
          <OrgSwitcher />
        </div>
      </div>

      {/* Top-right: avatar */}
      <div ref={menuRef} className="pointer-events-none absolute right-5 top-3 z-30">
        <div className="pointer-events-auto relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className={cn(
              "grid h-8 w-8 place-items-center rounded-full text-[10px] font-medium uppercase tracking-wider transition-colors",
              menuOpen
                ? "bg-foreground text-background"
                : "bg-foreground/[0.04] text-foreground/70 ring-1 ring-foreground/[0.05] hover:bg-foreground/[0.07]",
            )}
            aria-label="Profilo"
          >
            {userInitials ?? "HK"}
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 overflow-hidden rounded-[14px] bg-white/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_18px_50px_-14px_rgba(15,15,15,0.18)] animate-riseIn">
              <Link
                to="/profile"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-foreground/80 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
              >
                <User className="h-3.5 w-3.5 text-foreground/45" strokeWidth={1.5} />
                Profilo
              </Link>
              <div className="mx-3 h-px bg-foreground/[0.06]" />
              <button
                type="button"
                onClick={logout}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-foreground/60 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                Esci
              </button>
            </div>
          )}
        </div>
      </div>

      <CmdKHandler onOpen={() => setHistoryOpen(true)} />
      <HistoryEventBridge onOpen={() => setHistoryOpen(true)} />

      <HistoryPanel open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

function CmdKHandler({ onOpen }: { onOpen: () => void }) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);
  return null;
}

function HistoryEventBridge({ onOpen }: { onOpen: () => void }) {
  React.useEffect(() => {
    const handler = () => onOpen();
    window.addEventListener("herika:open-history", handler);
    return () => window.removeEventListener("herika:open-history", handler);
  }, [onOpen]);
  return null;
}

function HistoryPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Smart-mode chat è disattivata: mostriamo le ricerche recenti come surrogate
  const recents = useAppSelector(selectRecentSearches);
  const dispatch = useAppDispatch();

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-[8100] bg-foreground/15 backdrop-blur-[2px] transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        className={cn(
          "fixed right-0 top-0 z-[8200] flex h-dvh w-[340px] flex-col bg-background/95 backdrop-blur-2xl shadow-[inset_1px_0_0_rgba(255,255,255,0.7),-12px_0_40px_-8px_rgba(15,15,15,0.10)] transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <header className="flex items-center justify-between gap-3 p-5">
          <h2 className="font-heading text-xs uppercase tracking-[0.22em] text-muted">
            Cronologia
          </h2>
          <div className="flex items-center gap-2">
            {recents.length > 0 && (
              <button
                onClick={() => dispatch(clearRecent())}
                className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted hover:border-error/40 hover:text-error"
                title="Pulisci ricerche recenti"
              >
                Pulisci
              </button>
            )}
            <button
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-full text-muted hover:bg-grey-bg hover:text-foreground"
              aria-label="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {recents.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted">Nessuna ricerca recente.</p>
          ) : (
            <ul className="space-y-0.5">
              {recents.map((r) => (
                <li key={r.id} className="group relative">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex w-full items-start gap-2 truncate rounded-[10px] py-2 pl-3 pr-9 text-left text-sm text-foreground hover:bg-grey-bg"
                  >
                    <History className="mt-0.5 h-3 w-3 shrink-0 text-muted" />
                    <div className="min-w-0">
                      <div className="truncate">{r.destination_name}</div>
                      <div className="truncate text-[11px] text-muted">
                        {formatDateRange(r.check_in, r.check_out)}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover:opacity-100"
                    aria-label="Elimina"
                    title="Elimina"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}
